import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ExitToApp as LogoutIcon,
  Home as HomeIcon,
  FitnessCenter as ActivityIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { userService, getPhotoUrl, baseURL } from '../../services/api';
import PessoaForm from '../Pessoa/PessoaForm';
import { useAuth } from '../../contexts/auth';
import { log } from '../../utils/logger';

const drawerWidth = 240;

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, validateToken, setUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentUserName, setCurrentUserName] = useState('');
  const [userPhoto, setUserPhoto] = useState(null);

  // Função para atualizar informações do usuário - usando useCallback para evitar recriações desnecessárias
  const updateUserInfo = useCallback(async () => {
    try {
      setLayoutLoading(true);
      log('MainLayout: Atualizando informações do usuário...', null, 'info', true);
      
      // Validar o token e atualizar as informações do usuário
      const isValid = await validateToken();
      
      if (isValid) {
        log('MainLayout: Token validado com sucesso', null, 'info', true);
      } else {
        log('MainLayout: Token inválido, redirecionando para login', null, 'warn', true);
        navigate('/login', {
          state: { authError: 'Sessão expirada. Por favor, faça login novamente.' },
          replace: true
        });
      }
    } catch (error) {
      log('MainLayout: Erro ao atualizar informações do usuário:', error, 'error', true);
    } finally {
      setLayoutLoading(false);
    }
  }, [validateToken, navigate]);

  // Efeito para atualizar a foto do usuário quando o contexto de autenticação mudar
  useEffect(() => {
    if (user) {
      log('Atualizando informações do usuário no layout', null, 'info', true);
      setCurrentUserName(user.name || '');
      
      // Atualizar o estado do usuário para garantir que temos os dados mais recentes
      updateUserInfo();
    }
  }, [user, updateUserInfo]);

  useEffect(() => {
    if (user && user.photo) {
      // Adicionar timestamp para evitar cache
      const timestamp = new Date().getTime();
      const photoUrl = getPhotoUrl(user.photo) + `?t=${timestamp}`;
      setUserPhoto(photoUrl);
    } else {
      setUserPhoto(null);
    }
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    log('MainLayout: Iniciando processo de logout', null, 'info', true);
    signOut(); // Usar a função signOut do contexto de autenticação
    log('MainLayout: Logout concluído, redirecionando para login', null, 'info', true);
    navigate('/login', { replace: true });
  };

  const handleEditProfile = async () => {
    try {
      setLoading(true);
      const response = await userService.getById(user.id);
      setCurrentUserData(response.data);
      setOpenEditForm(true);
      handleProfileMenuClose();
    } catch (error) {
      log('Erro ao carregar dados do usuário:', error, 'error', true);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados do usuário. Por favor, tente novamente.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditFormClose = () => {
    setOpenEditForm(false);
    setCurrentUserData(null);
  };

  const validateFormData = (formData) => {
    const errors = {};
    const name = formData.get('name');
    const email = formData.get('email');
    const photo = formData.get('photo');

    if (!name || name.trim() === '') {
      errors.name = 'Nome é obrigatório';
    }
    if (!email || email.trim() === '') {
      errors.email = 'Email é obrigatório';
    }
    if (photo && photo instanceof File) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!allowedTypes.includes(photo.type)) {
        errors.photo = 'Formato de imagem inválido. Use JPG, PNG ou GIF';
      }
      if (photo.size > maxSize) {
        errors.photo = 'Imagem muito grande. Máximo permitido: 5MB';
      }
    }

    return errors;
  };

  const handleEditFormSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const errors = validateFormData(formData);
    
    if (Object.keys(errors).length > 0) {
      setSnackbar({
        open: true,
        message: Object.values(errors)[0],
        severity: 'error'
      });
      return;
    }
    
    try {
      setLoading(true);

      // Verificar se formData é um objeto FormData válido
      if (!(formData instanceof FormData)) {
        const newFormData = new FormData();
        Object.keys(formData).forEach(key => {
          if (formData[key] !== null && formData[key] !== undefined) {
            if (key === 'photo' && formData[key] instanceof File) {
              newFormData.append('photo', formData[key]);
            } else {
              newFormData.append(key, formData[key]);
            }
          }
        });
        formData = newFormData;
      }

      log('Dados do formulário a serem enviados:', null, 'info', true);
      for (let pair of formData.entries()) {
        log(pair[0] + ': ' + pair[1], null, 'info', true);
      }

      const response = await userService.update(user.id, formData);
      log('Resposta do servidor após atualização:', response.data, 'info', true);
      
      // Atualiza os dados do usuário no contexto de autenticação
      await validateToken();
      
      setOpenEditForm(false);
      setCurrentUserData(null);
      
      // Notifica o usuário do sucesso
      setSnackbar({
        open: true,
        message: 'Perfil atualizado com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      log('Erro ao atualizar dados do usuário:', error, 'error', true);
      if (error.response) {
        log('Detalhes do erro:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        }, 'error', true);
      }
      
      // Notifica o usuário do erro
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao atualizar perfil. Por favor, tente novamente.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const canAccessLares = () => {
    return user && (user.profile === 'Administrador' || user.profile === 'Gestor');
  };

  const isAdmin = () => {
    return user && user.profile === 'Administrador';
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', show: true },
    { text: 'Lares', icon: <HomeIcon />, path: '/lares', show: canAccessLares() },
    { text: 'Pessoas', icon: <PersonIcon />, path: '/pessoas', show: true },    
    { text: 'Diários', icon: <AssignmentIcon />, path: '/diary', show: true },
    { text: 'Atividades', icon: <ActivityIcon />, path: '/atividades', show: isAdmin() }
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Menus
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems
          .filter(item => item.show)
          .map((item) => (
            <ListItem
              key={item.text}
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
      </List>
    </div>
  );

  // Se o layout estiver carregando, mostrar um indicador de carregamento
  if (layoutLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Carregando interface...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Sistema TLC - Diário Digital da Pessoa Idosa
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar 
              src={userPhoto}
              alt={currentUserName || 'Avatar'}
              sx={{ 
                bgcolor: 'secondary.main',
                width: 40,
                height: 40
              }}
            >
              {!user?.photo && (currentUserName?.charAt(0) || 'U')}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="subtitle1">{currentUserName}</Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">{user?.profile}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleEditProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Meu Perfil</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sair</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Dialog para edição do perfil */}
      <PessoaForm
        open={openEditForm}
        onClose={handleEditFormClose}
        onSubmit={handleEditFormSubmit}
        pessoa={currentUserData}
        mode="edit"
        loading={loading}
        setUser={setUser}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainLayout;
